/**
 * Scroll positioning for homepage section cues.
 *
 * Desktop: demo frame vertical center = viewport center (custom eased glide).
 * Mobile: section eyebrow sits just below the fixed header (same glide pattern).
 */

let activeScrollAnimationFrame = null;

/** Bottom edge of everything fixed in the header chrome (logo transform overflow included). */
export function readFixedHeaderObstructionBottom() {
  if (typeof window === 'undefined') return 0;

  const headerElement = document.querySelector('.header');
  if (!headerElement) return 0;

  const obstructionBottoms = [
    headerElement.getBoundingClientRect().bottom,
    headerElement.querySelector('.nav-brand-logo')?.getBoundingClientRect().bottom,
    headerElement.querySelector('.nav-brand')?.getBoundingClientRect().bottom,
    headerElement.querySelector('.nav')?.getBoundingClientRect().bottom,
    headerElement.querySelector('.nav-toggle')?.getBoundingClientRect().bottom,
  ].filter((value) => Number.isFinite(value));

  if (obstructionBottoms.length === 0) return 0;
  return Math.max(...obstructionBottoms);
}

/** @deprecated Use readFixedHeaderObstructionBottom */
export function readFixedHeaderObstructionBottomPixels() {
  return readFixedHeaderObstructionBottom();
}

/** @deprecated Use readFixedHeaderObstructionBottom */
export function readFixedHeaderBottomPixels() {
  return readFixedHeaderObstructionBottom();
}

/** Computed section scroll-margin when available (resolved by the browser for this device). */
export function readSiteHeaderScrollOffsetPixels() {
  if (typeof window === 'undefined') return 0;

  const probeSection = document.querySelector('section[id]');
  if (probeSection) {
    const scrollMarginTop = parseFloat(window.getComputedStyle(probeSection).scrollMarginTop);
    if (Number.isFinite(scrollMarginTop) && scrollMarginTop > 0) {
      return scrollMarginTop;
    }
  }

  return readFixedHeaderObstructionBottom();
}

function isMobileViewport() {
  return (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 48rem)').matches
  );
}

function getSectionEyebrowElement(sectionElement) {
  return sectionElement.querySelector('.home-showcase-eyebrow');
}

/** Animation viewport — marketingDemos.css “Demo animation plays inside this box”. */
export function getSectionScrollVisualFrameElement(sectionElement) {
  if (!sectionElement) return null;

  return (
    sectionElement.querySelector('.mkt-demo-visual') ||
    sectionElement.querySelector('.mkt-cta-control-visual') ||
    sectionElement.querySelector('.mkt-cta-photo-visual-image') ||
    sectionElement.querySelector('.mkt-cta-photo-visual') ||
    sectionElement.querySelector('.mkt-demo-stage')
  );
}

/** Viewport vertical center in the same coordinate space as getBoundingClientRect().top */
export function readViewportVerticalCenterYPixels() {
  const visualViewport = window.visualViewport;
  if (visualViewport) {
    return visualViewport.offsetTop + visualViewport.height / 2;
  }
  return window.innerHeight / 2;
}

export function readVisualFrameCenterYPixels(sectionElement) {
  const frameElement = getSectionScrollVisualFrameElement(sectionElement) || sectionElement;
  const frameRect = frameElement.getBoundingClientRect();
  return frameRect.top + frameRect.height / 2;
}

export function readVisualFrameCenterAlignmentGap(sectionElement, viewportCenterY) {
  const centerY = viewportCenterY ?? readViewportVerticalCenterYPixels();
  return readVisualFrameCenterYPixels(sectionElement) - centerY;
}

function readEyebrowClearanceGap(eyebrowElement, headerBottomY) {
  const headerBottom = headerBottomY ?? readFixedHeaderObstructionBottom();
  return eyebrowElement.getBoundingClientRect().top - headerBottom;
}

function readSectionTopClearanceGap(sectionElement, headerBottomY) {
  const headerBottom = headerBottomY ?? readFixedHeaderObstructionBottom();
  return sectionElement.getBoundingClientRect().top - headerBottom;
}

function readMaxDocumentScrollY() {
  return Math.max(
    0,
    document.documentElement.scrollHeight - window.innerHeight,
  );
}

function easeInOutCubic(progress) {
  return progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - (-2 * progress + 2) ** 3 / 2;
}

function cancelActiveScrollAnimation() {
  if (activeScrollAnimationFrame != null) {
    cancelAnimationFrame(activeScrollAnimationFrame);
    activeScrollAnimationFrame = null;
  }
}

/** Direct scroll — bypasses html { scroll-behavior: smooth } so frames land exactly. */
function setWindowScrollY(scrollY) {
  const clampedScrollY = Math.min(readMaxDocumentScrollY(), Math.max(0, scrollY));
  const rootElement = document.documentElement;
  const previousScrollBehavior = rootElement.style.scrollBehavior;
  rootElement.style.scrollBehavior = 'auto';
  window.scrollTo(0, clampedScrollY);
  rootElement.style.scrollBehavior = previousScrollBehavior;
}

function animateScrollByGap({
  readGap,
  durationMs = 720,
}) {
  cancelActiveScrollAnimation();

  const startGap = readGap();

  if (Math.abs(startGap) < 0.5) {
    return;
  }

  const startTime = performance.now();
  const travelDurationMs = Math.min(
    durationMs,
    Math.max(420, Math.abs(startGap) * 0.65),
  );

  const step = (now) => {
    const elapsed = now - startTime;
    const progress = Math.min(1, elapsed / travelDurationMs);
    const easedProgress = easeInOutCubic(progress);
    const targetRemainingGap = startGap * (1 - easedProgress);
    const currentGap = readGap();
    const scrollAdjust = currentGap - targetRemainingGap;

    if (Math.abs(scrollAdjust) >= 0.5) {
      setWindowScrollY(window.scrollY + scrollAdjust);
    }

    if (progress < 1) {
      activeScrollAnimationFrame = requestAnimationFrame(step);
      return;
    }

    activeScrollAnimationFrame = null;
  };

  activeScrollAnimationFrame = requestAnimationFrame(step);
}

function scrollByGapInstant(readGap) {
  const gap = readGap();
  if (Math.abs(gap) < 0.5) {
    return;
  }
  setWindowScrollY(window.scrollY + gap);
}

function scrollMobileSectionIntoView(sectionElement, useSmooth) {
  const lockedHeaderBottom = readFixedHeaderObstructionBottom();
  const eyebrowElement = getSectionEyebrowElement(sectionElement);
  const readGap = eyebrowElement
    ? () => readEyebrowClearanceGap(eyebrowElement, lockedHeaderBottom)
    : () => readSectionTopClearanceGap(sectionElement, lockedHeaderBottom);

  if (!useSmooth) {
    scrollByGapInstant(readGap);
    return;
  }

  animateScrollByGap({ readGap });
}

function scrollDesktopSectionIntoView(sectionElement, useSmooth) {
  const lockedViewportCenterY = readViewportVerticalCenterYPixels();
  const readGap = () =>
    readVisualFrameCenterAlignmentGap(sectionElement, lockedViewportCenterY);

  if (!useSmooth) {
    scrollByGapInstant(readGap);
    return;
  }

  animateScrollByGap({ readGap });
}

/** Inset used by scroll-cue visibility — matches how each viewport scrolls. */
export function readEffectiveScrollHeaderOffsetPixels(sectionElement) {
  if (isMobileViewport()) {
    return readFixedHeaderObstructionBottom();
  }

  const scrollMarginTop = sectionElement
    ? parseFloat(window.getComputedStyle(sectionElement).scrollMarginTop) || 0
    : 0;

  return scrollMarginTop || readSiteHeaderScrollOffsetPixels();
}

/** Glide to the section using viewport-specific alignment. */
export function scrollToSectionStart(sectionId, { behavior = 'smooth' } = {}) {
  const sectionElement = document.getElementById(sectionId);
  if (!sectionElement || typeof window === 'undefined') return;

  if (isMobileViewport()) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const useSmooth = !reduceMotion && behavior === 'smooth';
    cancelActiveScrollAnimation();
    scrollMobileSectionIntoView(sectionElement, useSmooth);
    return;
  }

  const frameElement = getSectionScrollVisualFrameElement(sectionElement);
  if (!frameElement) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const useSmooth = !reduceMotion && behavior === 'smooth';

  cancelActiveScrollAnimation();
  scrollDesktopSectionIntoView(sectionElement, useSmooth);
}
